import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api, { apiError, fileUrl } from "@/lib/api";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { CheckCircle, DotsSixVertical, ShieldCheck, XCircle, Clock, SpinnerGap, ArrowRight, ArrowLeft, Copy, Broadcast, Lock } from "@phosphor-icons/react";

function Countdown({ endsAt }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const diff = new Date(endsAt).getTime() - now;
  if (diff <= 0) return <span className="font-mono text-xs text-red-500">CLOSING</span>;
  const h = Math.floor(diff / 3.6e6), m = Math.floor((diff % 3.6e6) / 6e4), s = Math.floor((diff % 6e4) / 1000);
  return <span className="font-mono text-xs text-zinc-500">CLOSES IN {h}h {m}m {s}s</span>;
}

function SortableCandidate({ c, index }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: c.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.9 : 1, scale: isDragging ? 1.02 : 1, zIndex: isDragging ? 10 : 1 };
  return (
    <div ref={setNodeRef} style={style} data-testid={`rank-item-${c.id}`} className={`flex items-center gap-3 bg-white border p-3.5 rounded-lg ${isDragging ? "shadow-xl border-[#1F6FE5]" : "border-zinc-200"}`}>
      <div className="w-6 h-6 rounded-full bg-[#1F6FE5] text-white text-xs font-bold flex items-center justify-center shrink-0">{index + 1}</div>
      <div className="w-9 h-9 rounded-full bg-zinc-100 overflow-hidden flex items-center justify-center shrink-0 text-sm font-bold text-zinc-400">
        {c.photo_url ? <img src={fileUrl(c.photo_url)} alt="" className="w-full h-full object-cover" /> : c.name[0]}
      </div>
      <div className="flex-1 min-w-0"><div className="font-medium text-sm truncate">{c.name}</div>{c.bio && <div className="text-xs text-zinc-400 truncate">{c.bio}</div>}</div>
      <button {...attributes} {...listeners} className="text-zinc-300 hover:text-zinc-500 cursor-grab touch-none active:cursor-grabbing"><DotsSixVertical size={22} weight="bold" /></button>
    </div>
  );
}

function Shell({ org, children }) {
  const brand = org?.branding?.primary_color || "#1F6FE5";
  return (
    <div className="min-h-[100dvh] bg-[#F7F8F9] flex flex-col">
      <header className="backdrop-blur-xl bg-white/80 border-b border-zinc-200 sticky top-0 z-20">
        <div className="max-w-lg mx-auto px-6 h-14 flex items-center gap-2">
          {org?.branding?.logo_url ? <img src={fileUrl(org.branding.logo_url)} alt="" className="h-7 w-auto" /> : <div className="w-6 h-6 rounded-[4px]" style={{ background: brand }} />}
          <span className="font-semibold text-sm">{org?.name}</span>
          <span className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-400"><ShieldCheck size={13} weight="fill" style={{ color: brand }} /> Secure</span>
        </div>
      </header>
      <div className="flex-1 max-w-lg w-full mx-auto px-6 py-8">{children}</div>
      <footer className="max-w-lg w-full mx-auto px-6 pb-8">
        <p data-testid="vote-privacy" className="text-[11px] leading-relaxed text-zinc-400 border-t border-zinc-100 pt-3">
          <strong className="text-zinc-500">Privacy:</strong> {org?.name || "The organiser"} is the data controller; SelectFlow is the processor. Your ballot is anonymous — it is never linked to your name, contact details, IP address or the time you voted. Your one-time voting link is used only to check eligibility and prevent double voting.
        </p>
      </footer>
    </div>
  );
}

function Message({ org, icon: Icon, color, title, body, action }) {
  return (
    <Shell org={org}>
      <div className="mt-16 text-center vf-fade-up">
        <Icon size={56} weight="fill" className="mx-auto mb-5" style={{ color }} />
        <h1 className="font-display font-bold text-2xl tracking-tight mb-2">{title}</h1>
        <p className="text-zinc-500">{body}</p>
        {action}
      </div>
    </Shell>
  );
}

export default function Vote() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const kioskId = sp.get("kiosk");
  const [state, setState] = useState({ status: "loading" });
  const [step, setStep] = useState(0);
  const [choices, setChoices] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [copied, setCopied] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => {
    if (!receipt || !kioskId) return;
    const t = setTimeout(() => { window.location.replace(`/kiosk/${kioskId}`); }, 8000);
    return () => clearTimeout(t);
  }, [receipt, kioskId]);

  // Kiosk hygiene: release an abandoned/idle ballot so it can't be replayed by the next voter.
  const releaseAndReturn = async () => {
    try { await api.post(`/public/kiosk/release/${token}`); } catch { /* best effort */ }
    window.location.replace(`/kiosk/${kioskId}`);
  };
  useEffect(() => {
    if (!kioskId || receipt || !state.valid) return;
    let timer;
    const reset = () => { clearTimeout(timer); timer = setTimeout(() => { releaseAndReturn(); }, 90000); };
    const evts = ["pointerdown", "keydown", "mousemove", "touchstart"];
    evts.forEach((e) => window.addEventListener(e, reset));
    reset();
    return () => { clearTimeout(timer); evts.forEach((e) => window.removeEventListener(e, reset)); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kioskId, receipt, state.valid, token]);

  const kioskReturn = kioskId ? (
    <button data-testid="kiosk-next-voter" onClick={() => { window.location.replace(`/kiosk/${kioskId}`); }}
      className="mt-6 inline-flex items-center gap-2 px-5 py-3 bg-black text-white rounded-md font-semibold text-sm hover:bg-zinc-800 transition-colors active:scale-95">
      Next voter
    </button>
  ) : null;

  useEffect(() => {
    api.get(`/vote/${token}`).then(({ data }) => {
      setState(data);
      if (data.valid) {
        const init = {};
        data.positions.forEach((p) => { if (p.ballot_type === "ranked") init[p.id] = p.candidates.map((c) => c.id); });
        setChoices(init);
      }
    }).catch(() => setState({ valid: false, reason: "invalid" }));
  }, [token]);

  if (state.status === "loading") return <div className="min-h-[100dvh] flex items-center justify-center text-zinc-400 font-mono text-sm">loading…</div>;

  if (!state.valid) {
    const map = {
      invalid: { icon: XCircle, color: "#dc2626", title: "Invalid voting link", body: "This link is not valid. Please check the link in your invitation." },
      already_voted: { icon: CheckCircle, color: "#059669", title: "You've already voted", body: "This voting link has already been used. Each voter can vote once." },
      not_open: { icon: Clock, color: "#d97706", title: "Voting hasn't opened yet", body: state.starts_at ? `Voting opens ${new Date(state.starts_at).toLocaleString()}.` : "Please come back later." },
      closed: { icon: XCircle, color: "#dc2626", title: "Voting has closed", body: "This election is no longer accepting votes." },
    };
    return <Message org={state.org} {...(map[state.reason] || map.invalid)} action={kioskReturn} />;
  }

  if (receipt) {
    return (
      <Shell org={state.org}>
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 200, damping: 18 }} className="mt-12 text-center">
          <CheckCircle size={64} weight="fill" className="mx-auto mb-5 text-emerald-500" />
          <h1 className="font-display font-bold text-3xl tracking-tight mb-2">Vote recorded</h1>
          <p className="text-zinc-500 mb-8">Your ballot was cast anonymously. Keep your receipt code as proof of participation.</p>
          <div className="bg-white border border-zinc-200 rounded-xl p-6">
            <div className="text-xs uppercase tracking-widest text-zinc-400 mb-2">Receipt code</div>
            <div data-testid="receipt-code" className="font-mono text-2xl font-bold tracking-widest">{receipt}</div>
            <button
              data-testid="copy-receipt"
              onClick={() => { try { navigator.clipboard.writeText(receipt); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { setCopied(true); setTimeout(() => setCopied(false), 1500); } }}
              className="mt-4 inline-flex items-center gap-1.5 text-sm text-[#1F6FE5] font-medium"
            >
              <Copy size={15} /> {copied ? "Copied!" : "Copy code"}
            </button>
          </div>
          {!kioskId && state.election?.id && state.election?.live_monitor && (
            <a
              data-testid="watch-live-btn"
              href={`/live/${state.election.id}`}
              target="_blank"
              rel="noreferrer"
              className="mt-6 inline-flex items-center gap-2 px-5 py-3 bg-black text-white rounded-md font-semibold text-sm hover:bg-zinc-800 transition-colors active:scale-95"
            >
              <Broadcast size={17} weight="fill" /> Watch live results
            </a>
          )}
          {kioskId && (
            <div>
              {kioskReturn}
              <p className="mt-3 text-xs text-zinc-400">This screen resets automatically for the next voter.</p>
            </div>
          )}
        </motion.div>
      </Shell>
    );
  }

  const positions = state.positions;
  const reviewing = step >= positions.length;
  const progress = Math.round(((reviewing ? positions.length : step) / positions.length) * 100);

  const pick = (pid, cid) => setChoices({ ...choices, [pid]: cid });
  const onDragEnd = (pid) => ({ active, over }) => {
    if (over && active.id !== over.id) {
      const arr = choices[pid];
      setChoices({ ...choices, [pid]: arrayMove(arr, arr.indexOf(active.id), arr.indexOf(over.id)) });
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const ballots = positions.map((p) => ({
        position_id: p.id,
        selections: p.ballot_type === "ranked" ? choices[p.id] : (choices[p.id] ? [choices[p.id]] : []),
      })).filter((b) => b.selections.length > 0);
      const { data } = await api.post(`/vote/${token}`, { ballots });
      setReceipt(data.receipt);
    } catch (e) { setState({ valid: false, reason: apiError(e.response?.data?.detail).includes("already") ? "already_voted" : "invalid" }); }
    finally { setSubmitting(false); }
  };

  const brand = state.org?.branding?.primary_color || "#1F6FE5";

  return (
    <Shell org={state.org}>
      {kioskId && (
        <button data-testid="kiosk-cancel" onClick={releaseAndReturn}
          className="fixed top-4 right-4 z-50 inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-zinc-300 rounded-md text-xs font-semibold text-zinc-600 hover:border-black transition-colors shadow-sm">
          Cancel & start over
        </button>
      )}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-mono text-zinc-400">{reviewing ? "REVIEW" : `${step + 1} / ${positions.length}`}</span>
          {state.election.ends_at && <Countdown endsAt={state.election.ends_at} />}
        </div>
        <div className="h-1.5 bg-zinc-200 rounded-full overflow-hidden">
          <div className="h-full transition-all duration-500" style={{ width: `${progress}%`, background: brand }} />
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }}>
          {step === 0 && <h1 className="font-display font-bold text-2xl tracking-tight mb-6">{state.election.title}</h1>}

          {!reviewing ? (
            <div>
              <h2 className="font-display font-semibold text-xl tracking-tight">{positions[step].title}</h2>
              {positions[step].description && <p className="text-sm text-zinc-500 mt-1 mb-4">{positions[step].description}</p>}
              <p className="text-xs text-zinc-400 mb-4">{positions[step].ballot_type === "ranked" ? "Drag to rank from most to least preferred." : "Select one candidate."}</p>

              {positions[step].ballot_type === "ranked" ? (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd(positions[step].id)}>
                  <SortableContext items={choices[positions[step].id] || []} strategy={verticalListSortingStrategy}>
                    <div className="space-y-2.5">
                      {(choices[positions[step].id] || []).map((cid, i) => {
                        const c = positions[step].candidates.find((x) => x.id === cid);
                        return <SortableCandidate key={cid} c={c} index={i} />;
                      })}
                    </div>
                  </SortableContext>
                </DndContext>
              ) : (
                <div className="space-y-3">
                  {positions[step].candidates.map((c) => {
                    const selected = choices[positions[step].id] === c.id;
                    return (
                      <button
                        key={c.id}
                        data-testid={`vote-option-${c.id}`}
                        onClick={() => pick(positions[step].id, c.id)}
                        className={`w-full flex items-center gap-3.5 p-4 rounded-xl text-left transition-colors ${selected ? "bg-blue-50/60" : "bg-white hover:bg-zinc-50"}`}
                        style={{ border: selected ? `2px solid ${brand}` : "1px solid #e4e4e7" }}
                      >
                        <div className="w-11 h-11 rounded-full bg-zinc-100 overflow-hidden flex items-center justify-center shrink-0 text-base font-bold text-zinc-400">
                          {c.photo_url ? <img src={fileUrl(c.photo_url)} alt="" className="w-full h-full object-cover" /> : c.name[0]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold">{c.name}</div>
                          {c.bio && <div className="text-sm text-zinc-500 line-clamp-2">{c.bio}</div>}
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 shrink-0 flex items-center justify-center`} style={{ borderColor: selected ? brand : "#d4d4d8" }}>
                          {selected && <div className="w-2.5 h-2.5 rounded-full" style={{ background: brand }} />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div>
              <h2 className="font-display font-semibold text-xl tracking-tight mb-4">Review your choices</h2>
              <div className="space-y-3">
                {positions.map((p) => {
                  const ch = choices[p.id];
                  let text;
                  if (p.ballot_type === "ranked") text = (ch || []).map((cid, i) => `${i + 1}. ${p.candidates.find((c) => c.id === cid)?.name}`).join(" · ");
                  else text = ch ? p.candidates.find((c) => c.id === ch)?.name : "No selection";
                  return (
                    <div key={p.id} data-testid={`review-${p.id}`} className="bg-white border border-zinc-200 rounded-lg p-4">
                      <div className="text-xs uppercase tracking-widest text-zinc-400 mb-1">{p.title}</div>
                      <div className="text-sm font-medium">{text || <span className="text-zinc-400">No selection</span>}</div>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-zinc-400 mt-4 text-center">Once submitted, your ballot is final and cannot be changed.</p>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="mt-8 sticky bottom-4">
        <div className="flex justify-center mb-3">
          <span data-testid="secret-ballot-badge" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#F3EFEA] border border-line text-xs font-medium text-zinc-600">
            <Lock size={13} weight="fill" className="text-zinc-500" /> Secret ballot
          </span>
        </div>
        <div className="flex gap-3">
        {step > 0 && (
          <button data-testid="vote-back" onClick={() => setStep(step - 1)} className="inline-flex items-center gap-1.5 px-5 py-3.5 bg-white border border-zinc-300 rounded-full font-semibold hover:border-black transition-colors active:scale-95">
            <ArrowLeft size={18} weight="bold" /> Back
          </button>
        )}
        {!reviewing ? (
          <button
            data-testid="vote-next"
            onClick={() => setStep(step + 1)}
            disabled={positions[step].ballot_type === "single_choice" && !choices[positions[step].id]}
            className="flex-1 inline-flex items-center justify-center gap-2 py-3.5 text-white rounded-full font-semibold transition-colors active:scale-[0.98] disabled:opacity-40"
            style={{ background: brand }}
          >
            {step + 1 === positions.length ? "Review" : "Next"} <ArrowRight size={18} weight="bold" />
          </button>
        ) : (
          <button
            data-testid="vote-submit"
            onClick={submit}
            disabled={submitting}
            className="flex-1 inline-flex items-center justify-center gap-2 py-3.5 text-white rounded-full font-semibold transition-colors active:scale-[0.98] disabled:opacity-70"
            style={{ background: brand }}
          >
            {submitting ? <><SpinnerGap size={18} className="animate-spin" /> Submitting…</> : "Cast my vote"}
          </button>
        )}
        </div>
      </div>
    </Shell>
  );
}
